'use client';

import type {FC} from 'react';
import {useCallback, useEffect, useId, useRef, useState} from 'react';
import {FiChevronDown} from 'react-icons/fi';
import clsx from 'clsx';
import Link from 'next/link';

import {DOCUMENT_TYPE_ABOUT_FRONTPAGE} from '@/modules/finkn/about/frontpage/sanity/constants';
import {DOCUMENT_TYPE_SEARCH_FRONTPAGE} from '@/modules/finkn/search/sanity/constants';
import {SearchIcon} from '@/modules/foundation/components/icons/search-icon';
import {useEventListener} from '@/modules/foundation/hooks/useEventListener';
import {internalLinkToHref} from '@/modules/foundation/sanity/link/query';

import {Hamburger} from './Hamburger';
import {LoginButton} from './LoginButton';
import {NavArticleCard} from './NavArticleCard';
import type {Navigation, NavItem} from './Navbar';
import {NavLink} from './NavLink';

const MENU_STRING = 'Meny';

type Props = {
	// navigation: NavigationType;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	navigation: Navigation;
};

export const MobileNav: FC<Props> = ({navigation}) => {
	const listRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const navRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
	const {loginFrontpageTitle} = navigation;

	/**
	 * If user click outside the navigation,
	 * close the mobile navigation
	 */
	const handleOutsideClick = (event: MouseEvent) => {
		if (!isOpen) return;

		if (!navRef.current?.contains(event.target as Node)) {
			setIsOpen(false);
			setActiveDropdown(null);
		}
	};
	useEventListener('click', handleOutsideClick);

	/**
	 * If user press Escape key, close the mobile navigation
	 * and set focus on the trigger
	 */
	const handleEscape = (event: KeyboardEvent) => {
		if (!isOpen) return;

		if (event.key === 'Escape') {
			setIsOpen(false);
			setActiveDropdown(null);
			triggerRef.current?.focus();
		}
	};
	useEventListener('keydown', handleEscape);

	/**
	 * On first focus inside the navRef,
	 * set focusInsideNav to true
	 */
	const handleFocus = (event: FocusEvent) => {
		const insideNav = navRef.current?.contains(event.target as Node);
		if (!insideNav && isOpen) {
			setIsOpen(false);
			setActiveDropdown(null);
		}
	};
	useEventListener('focusin', handleFocus);

	/**
	 * If user opens the mobile navigation, scroll the listRef
	 * to the top
	 */
	useEffect(() => {
		if (isOpen) {
			listRef.current?.scrollTo(0, 0);
		}
	}, [isOpen]);

	const handleClick = useCallback(() => {
		if (isOpen) {
			setActiveDropdown(null);
			setIsOpen(false);
		} else {
			setIsOpen(true);
		}
	}, [isOpen]);

	const handleDropdownClick = useCallback(
		(key: string) => {
			if (activeDropdown === key) {
				setActiveDropdown(null);
			} else {
				setActiveDropdown(key);
			}
		},
		[activeDropdown],
	);

	const closeNav = useCallback(() => {
		setIsOpen(false);
		setActiveDropdown(null);
	}, []);

	return (
		<div className={clsx('min-[1070px]:hidden')} ref={navRef}>
			<div className={clsx('flex', 'justify-center', 'items-center')}>
				<Link
					href={internalLinkToHref(DOCUMENT_TYPE_SEARCH_FRONTPAGE)}
					onClick={closeNav}
					className={clsx(
						'w-12',
						'h-12',
						'flex',
						'justify-end',
						'items-center',
						'relative',
						'-right-2.5',
					)}
				>
					<SearchIcon aria-hidden className={clsx('text-lg', 'mr-2')} />
					<span className="sr-only">Søk</span>
				</Link>
				{/**
				 * The clickarea should be at least 48x48px for accessibility
				 */}
				<button
					ref={triggerRef}
					onClick={handleClick}
					aria-expanded={isOpen}
					className={clsx(
						'w-12',
						'h-12',
						'flex',
						'justify-center',
						'items-center',
						'relative',
						'-right-2.5',
					)}
				>
					<span className={clsx('sr-only')}>{MENU_STRING}</span>
					<Hamburger isOpen={isOpen} />
				</button>
			</div>
			<div
				ref={listRef}
				className={clsx(
					isOpen ? 'flex' : 'hidden',
					'flex-col',
					'absolute',
					'top-full',
					'max-h-[calc(100dvh-60px)]',
					'left-0',
					'right-0',
					'bg-white',
					'overflow-y-auto',
					'py-6',
					'z-50',
					'animate-expand',
					'shadow-[0px_12px_16px_-4px_rgba(0,67,121,0.11)]',
				)}
			>
				<ul
					className={clsx('px-6', 'pb-4', 'flex', 'flex-col', 'gap-4')}
					aria-label="Fagområder"
				>
					<li>
						<DropdownButton
							_key={navigation.insurance.href}
							title="Forsikring"
							expanded={activeDropdown === navigation.insurance.href}
							onClick={handleDropdownClick}
						/>
						<NavItemComponent
							navItem={navigation.insurance}
							isOpen={activeDropdown === navigation.insurance.href}
							onLinkClick={closeNav}
						/>
					</li>
					<li>
						<DropdownButton
							_key={navigation.bank.href}
							title="Bank"
							expanded={activeDropdown === navigation.bank.href}
							onClick={handleDropdownClick}
						/>
						<NavItemComponent
							navItem={navigation.bank}
							isOpen={activeDropdown === navigation.bank.href}
							onLinkClick={closeNav}
						/>
					</li>
					<li>
						<DropdownButton
							_key={navigation.collection.href}
							title="Inkasso"
							expanded={activeDropdown === navigation.collection.href}
							onClick={handleDropdownClick}
						/>
						<NavItemComponent
							navItem={navigation.collection}
							isOpen={activeDropdown === navigation.collection.href}
							onLinkClick={closeNav}
						/>
					</li>
				</ul>

				<ul className={clsx('px-6', 'border-t', 'pt-4')}>
					<li>
						<Link
							href={internalLinkToHref(DOCUMENT_TYPE_ABOUT_FRONTPAGE)}
							onClick={closeNav}
							className={clsx(
								'hover:text-link',
								'transition-colors',
								'relative',
								'inline-block',
								'py-2',
								'text-[13px]',
							)}
						>
							Om oss
						</Link>
					</li>
					<li>
						<Link
							href={internalLinkToHref('forLawyers-frontpage')}
							onClick={closeNav}
							className={clsx(
								'hover:text-link',
								'transition-colors',
								'py-2',
								'inline-block',
								'text-[13px]',
							)}
						>
							{navigation.forLawyersFrontpageTitle}
						</Link>
					</li>
					<li>
						<Link
							href={internalLinkToHref('finance-frontpage')}
							onClick={closeNav}
							className={clsx(
								'hover:text-link',
								'transition-colors',
								'py-2',
								'inline-block',
								'text-[13px]',
							)}
						>
							{navigation.financeFrontpageTitle}
						</Link>
					</li>
					{navigation.nemndspraksisLink && (
						<li>
							<a
								href={navigation.nemndspraksisLink}
								onClick={closeNav}
								target="_blank"
								className={clsx(
									'hover:text-link',
									'transition-colors',
									'py-2',
									'inline-block',
									'text-[13px]',
								)}
							>
								Nemndpraksis
							</a>
						</li>
					)}

					<li className="mt-4">
						<LoginButton title={loginFrontpageTitle} onClick={closeNav} />
					</li>
				</ul>
			</div>
		</div>
	);
};

type DropdownButtonProps = {
	title: string;
	expanded: boolean;
	_key: string;
	onClick: (key: string) => void;
};

const DropdownButton: FC<DropdownButtonProps> = ({title, expanded, onClick, _key}) => {
	const handleClick = useCallback(() => {
		onClick(_key);
	}, [_key, onClick]);

	return (
		<button
			onClick={handleClick}
			className={clsx(
				'flex',
				'items-center',
				'gap-4',
				'w-full',
				'justify-between',
				'font-display',
				'text-base',
				'hover:text-primary-15',
				'transition-colors',
				'group',
				expanded ? 'font-medium text-primary-15' : 'text-primary-5',
			)}
		>
			{title}
			<FiChevronDown
				aria-hidden
				className={clsx(
					expanded ? 'rotate-180 text-primary-15' : 'text-secondary-40',
					'transition-all',
					'text-lg',
					'group-hover:text-primary-15',
				)}
			/>
		</button>
	);
};

const NavItemComponent = ({
	navItem,
	isOpen,
	onLinkClick,
}: {
	navItem: NavItem;
	isOpen: boolean;
	onLinkClick?: () => void;
}) => {
	const {areas, resources, articles} = navItem;

	return (
		<ul
			className={clsx(
				'flex-col',
				'border',
				'border-grey-80',
				'rounded-lg',
				'p-4',
				'mt-4',
				'ml-4',
				isOpen ? 'flex' : 'hidden',
			)}
		>
			<NavItemComponentList title="Temaer">
				<li>
					<NavLink href={navItem.href} featured onClick={onLinkClick}>
						Alt om {navItem.title.toLowerCase()}
					</NavLink>
				</li>
				{areas.links.map((link) => (
					<li key={link._key}>
						<NavLink href={link.href} onClick={onLinkClick}>
							{link.title}
						</NavLink>
					</li>
				))}
			</NavItemComponentList>

			<NavItemComponentList title="Ressurser">
				{resources.links.map((link, idx) => (
					<li key={link._key}>
						<NavLink href={link.href} featured={idx === 0} onClick={onLinkClick}>
							{link.title}
						</NavLink>
					</li>
				))}
			</NavItemComponentList>

			<NavItemComponentList title="Aktuell nemnd">
				{navItem.nemnd.links.map((link) => (
					<li key={link._key}>
						<NavLink href={link.href} onClick={onLinkClick}>
							{link.title}
						</NavLink>
					</li>
				))}
			</NavItemComponentList>

			{articles?.length ? (
				<NavItemComponentList title="Nyheter" hideTitle>
					{articles.map((article, idx) => (
						<li key={article._key}>
							<NavArticleCard
								image={idx === 0 ? article.imageWeb : undefined}
								title={article.title}
								description={article.description}
								href={article.href}
								className={clsx(idx !== 0 && ['border-t', 'pt-4', 'mt-2'])}
								onClick={onLinkClick}
							/>
						</li>
					))}
				</NavItemComponentList>
			) : null}
		</ul>
	);
};

type NavItemComponentListProps = {
	children: React.ReactNode;
	title: string;
	hideTitle?: boolean;
};

const NavItemComponentList = ({children, title, hideTitle}: NavItemComponentListProps) => {
	const id = useId();

	return (
		<li className="mb-10 last:mb-0">
			<strong
				id={id}
				className={clsx(
					'text-grey-15',
					'font-display',
					'text-[15px]',
					'font-medium',
					'block',
					'mb-2',
					hideTitle && 'sr-only',
				)}
			>
				{title}
			</strong>
			<ul aria-labelledby={id} className={clsx('grid', 'gap-2')}>
				{children}
			</ul>
		</li>
	);
};
